import { useQuery } from '@tanstack/react-query';

import type { GetMarkProductionCompleteResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get production complete status from a purchased vehicle
 */
export const getMarkProductionComplete = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkProductionCompleteResponse> => {
	const response = await apiClient.get<GetMarkProductionCompleteResponse>(
		ordersEndpoints.markProductionComplete(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting production complete status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkProductionComplete = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.markProductionComplete.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get production complete status without an order ID.');
			}
			return getMarkProductionComplete(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
